import { useMutation, useQuery } from "@apollo/client";
import { Editor } from "@tiptap/react";
import copy from "copy-to-clipboard";
import { compact } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  AskAiChatMessageFragmentFragment,
  EditSuggestedAssessmentResponseMutationMutation,
  EditSuggestedAssessmentResponseMutationMutationVariables,
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import cache, { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import InfoTooltip from "@components/tooltip/info-tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { pluralize, sanitizeMarkdown } from "@helpers/string";

import { ChatSessionContext } from "../ask-ai-context";
import editSuggestedAssessmentResponseMutation from "../graphql/edit-suggested-assessment-response-mutation";
import getAskAiSessionQuery from "../graphql/get-ask-ai-session-query";
import {
  createChatMessageNode,
  createChatMessageOutputItemNode,
} from "../helpers";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import { SuggestedAssessmentResponsesPlaceholderType } from "./ask-ai-placeholders";
import AskAICitationModal from "./citations-modal";

const AskAIEmbedSuggestedAssessmentResponses = ({
  suggestedAssessmentResponses,
  canBeEdited = false,
  chatSessionId,
  messageId,
}: {
  suggestedAssessmentResponses: SuggestedAssessmentResponsesPlaceholderType["responses"];
  canBeEdited?: boolean;
  chatSessionId?: number;
  messageId: string;
}) => {
  const [showCitationsModal, setShowCitationsModal] = useState(false);
  const responses = compact(suggestedAssessmentResponses);
  const [index, setIndex] = useState(0);
  const displayedResponse = responses[index];
  const citations = compact(displayedResponse?.citations || []);
  const { assessmentQuestionId, setCurrentSuggestedAssessmentResponseId } =
    useContext(ChatSessionContext);
  const label = useLabel();
  const location = useLocation<TFLocationState>();

  const { data: cachedChatSession } = useQuery<
    GetAskAiChatSessionQueryQuery,
    GetAskAiChatSessionQueryQueryVariables
  >(getAskAiSessionQuery, {
    variables: { chatSessionId: chatSessionId || 0 },
    fetchPolicy: "cache-only",
    skip: !chatSessionId,
  });
  const cachedAssessmentQuestionId = useMemo(
    () => cachedChatSession?.chatSession?.assessmentQuestion?.id,
    [cachedChatSession],
  );

  useEffect(() => {
    setCurrentSuggestedAssessmentResponseId?.(
      displayedResponse?.suggestionId || null,
    );
    return () => {
      setCurrentSuggestedAssessmentResponseId?.(null);
    };
  }, [
    displayedResponse?.suggestionId,
    setCurrentSuggestedAssessmentResponseId,
  ]);

  const isOnAssessmentPage = matchPath(
    location.pathname,
    "/assessments/assessment/:assessmentId",
  );

  const [editSuggestedAssessmentResponse, { loading: loadingEditResponse }] =
    useMutation<
      EditSuggestedAssessmentResponseMutationMutation,
      EditSuggestedAssessmentResponseMutationMutationVariables
    >(editSuggestedAssessmentResponseMutation);

  const handleClickEdit = () => {
    if (
      loadingEditResponse ||
      !chatSessionId ||
      !displayedResponse?.suggestionId
    )
      return;
    const cachedChatSession = cache.readQuery<
      GetAskAiChatSessionQueryQuery,
      GetAskAiChatSessionQueryQueryVariables
    >({
      query: getAskAiSessionQuery,
      variables: { chatSessionId },
    });
    const item: NonNullable<
      AskAiChatMessageFragmentFragment["outputItems"]
    >[number] = createChatMessageOutputItemNode({
      suggestedAssessmentResponses: [
        {
          __typename: "SuggestedAssessmentResponseNode",
          suggestedResponse: displayedResponse?.suggestedResponse || "",
          suggestionId: displayedResponse?.suggestionId || "",
          citations: displayedResponse?.citations || [],
        },
      ],
    });
    editSuggestedAssessmentResponse({
      variables: {
        chatSessionId,
        messageId,
        suggestionId: displayedResponse?.suggestionId,
      },
      optimisticResponse: {
        __typename: "Mutation",
        editSuggestedAssessmentResponse: {
          __typename: "EditSuggestedAssessmentResponseMutation",
          chatSession: cachedChatSession?.chatSession
            ? {
                ...(cachedChatSession?.chatSession || {}),
                messages: {
                  __typename: "ChatMessageConnection",
                  edges: [
                    ...(cachedChatSession?.chatSession?.messages?.edges || []),
                    {
                      __typename: "ChatMessageEdge",
                      node: createChatMessageNode({
                        outputItems: [item],
                      }),
                    },
                  ],
                },
              }
            : undefined,
        },
      },
    });
  };

  const handleClickInsertResponse = () => {
    const suggestedResponse = displayedResponse?.suggestedResponse || "";
    const elementId = assessmentQuestionId || cachedAssessmentQuestionId;
    if (!elementId || !suggestedResponse) return;
    const elSelector = `#assessment-question-${elementId} .ProseMirror[contenteditable="true"]`;
    const element = document.querySelector(elSelector) as HTMLDivElement & {
      editor: Editor;
    };
    if (element && element.editor) {
      // insert content at the end of the editor and focus on it
      const contentToInsert = `${
        element.editor.isEmpty ? "" : "\n\n"
      }${suggestedResponse}`;
      element.editor
        .chain()
        .focus("end")
        .insertContent(contentToInsert)
        .focus()
        .run();
    } else {
      copy(suggestedResponse);
      successNotificationVar({
        title: "Response Copied to Clipboard",
        description:
          "Sorry, we couldn't insert the response automatically, but it's copied to your clipboard, just paste it in the text field.",
        timeout: 10000,
      });
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <div className="font-medium text-sm text-gray-700 flex items-center gap-1.5">
          Suggested Responses
          <InfoTooltip
            text={`Suggested responses are generated using data from Topicflow and external integrations. These sources are visible to the reviewer during ${label("review", { pluralize: true })}, and to management when the ${label("review")} is delivered.`}
          />
        </div>
        {responses.length > 1 && (
          <div className="flex items-center gap-2">
            <Button
              icon={getTopicflowIcon("arrowLeft")}
              theme={buttonTheme.iconGray}
              size={ButtonSize.small}
              onClick={() => setIndex(index - 1)}
              disabled={index === 0}
            />
            <span className="text-gray-500">
              {index + 1}/{responses.length}
            </span>
            <Button
              icon={getTopicflowIcon("arrowRight")}
              theme={buttonTheme.iconGray}
              size={ButtonSize.small}
              onClick={() => setIndex(index + 1)}
              disabled={index === responses.length - 1}
            />
          </div>
        )}
      </div>
      <div className="border rounded-lg py-4 px-4 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <button
              className="text-gray-500"
              onClick={() => setShowCitationsModal(true)}
              disabled={citations.length === 0}
            >
              {`${citations.length} ${pluralize(
                "source",
                citations.length,
              )} used`}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {canBeEdited && chatSessionId && (
              <Button
                text="Edit"
                size={ButtonSize.medium}
                onClick={handleClickEdit}
              />
            )}
            <Button
              text="Insert Response"
              theme={buttonTheme.lightBlue}
              size={ButtonSize.medium}
              onClick={handleClickInsertResponse}
              disabled={!isOnAssessmentPage}
              tooltip={
                !isOnAssessmentPage
                  ? `You can only perform "Insert Response" on the ${label("review")} submission page`
                  : undefined
              }
            />
          </div>
        </div>
        <div className="prose prose-sm max-w-none">
          <AskAIMessageMarkdown>
            {sanitizeMarkdown(displayedResponse?.suggestedResponse || "")}
          </AskAIMessageMarkdown>
        </div>
      </div>
      {showCitationsModal && (
        <AskAICitationModal
          citations={citations}
          onClose={() => setShowCitationsModal(false)}
        />
      )}
      <div className="mt-2 prose prose-sm max-w-none">
        Let me know if you want me to tweak the wording or if you have other
        sources you'd like to use.
      </div>
    </div>
  );
};

export default AskAIEmbedSuggestedAssessmentResponses;
